import { formViewTypeAtom, newlyCreatedTemplateAtom } from '@/atoms/formFillingAtom';
import { useMutateCreatePDFTemplate } from '@/hooks/mutation/useMutateCreatePDFTemplate';
import { useMutateUpdateTemplate } from '@/hooks/mutation/useMutateUpdateTemplate';
import { generateFileKey, putToS3 } from '@/lib/s3';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { S3PresignedUrlMethod, TemplateVisibility } from '@heidi/schema/ml-scribe';
import { getCopyToastMessage } from '@heidi/templates';
import type { ExtractedField, PageDimensionsByPage } from '@heidi/templates/contracts';
import { type TemplateResponse } from '@heidi/templates/contracts';
import {
  extractedFieldsToFormFields,
  useGetUploadTemplateURL,
} from '@heidi/templates/data';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { isAxiosError } from 'axios';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FormViewType } from './view-form-modal.types';

/**
 * Everything the save needs, sourced explicitly by the caller. The form-editor
 * confirm flow builds this from the inline title + editor atoms; the legacy
 * `FormViewModal` builds it from its react-hook-form. Keeping the inputs
 * explicit is what lets the same create/edit/compliance orchestration back both
 * the modal and the modal-less "Confirm fields" flow.
 */
// eslint-disable-next-line @heidi/rules/no-new-carved-domain-data -- app-layer save orchestration input for FormViewModal + modal-less confirm; not @heidi/templates data
export interface SaveFormTemplateInput {
  name: string;
  visibility: TemplateVisibility;
  sharedTeamIds: string[];
  formFields: ExtractedField[];
  pageDimensions: PageDimensionsByPage;
  /** The uploaded/edited PDF file. Required to create; unused for in-place edits. */
  file: File | null;
  templateToEdit: TemplateResponse | null;
  /** CREATE vs EDIT intent — mirrors the legacy modal's `formViewType`. */
  formViewType: FormViewType | null;
}

type CreateTemplateOptions = {
  isCopyFromProtectedTemplate: boolean;
  copyToastMessage?: ReturnType<typeof getCopyToastMessage>;
};

/**
 * Headless create/update orchestration for PDF form templates, shared by the
 * `FormViewModal` and the modal-less "Confirm fields" flow. Handles the S3
 * upload, the protected-template "save as copy" branch, and the compliance retry
 * (`saveAnyway`). Compliance warning/block modals are driven by the mutation
 * hooks' atoms and rendered by the caller.
 *
 * `saveFormTemplate` resolves `true` on success and `false` on failure (it logs
 * and shows the destructive toast itself), so callers only navigate/close on a
 * truthy result.
 */
export const useSaveFormTemplate = () => {
  const intl = useIntl();
  // The team-details error toast used to be supplied HERE, because an
  // observer-borne port is last-render-wins across every observer of
  // `[GET_TEAM_DETAILS, { teamId }]` — leaving it undefined removed the toast
  // for everyone until the next render reinstalled it. It is bound once at
  // `configureWorkspaceData` now, so no observer carries it.
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();
  const setNewlyCreatedTemplate = useSetAtom(newlyCreatedTemplateAtom);
  const setFormViewType = useSetAtom(formViewTypeAtom);
  const { mutateAsync: getUploadTemplateURL } = useGetUploadTemplateURL();
  const { mutateAsync: createPDFTemplate } = useMutateCreatePDFTemplate();
  const { mutateAsync: updateTemplate } = useMutateUpdateTemplate();

  const uploadPdfToS3 = useCallback(
    async (objectKey: string, file: File) => {
      const presignedUrl = await getUploadTemplateURL({
        object_key: objectKey,
        method: S3PresignedUrlMethod.put_object,
      });

      if (!presignedUrl) {
        throw new Error('Failed to get presigned URL');
      }

      const response = await putToS3(file, presignedUrl);
      if (response.status !== 200) {
        throw new Error('Failed to upload PDF');
      }
    },
    [getUploadTemplateURL]
  );

  const editTemplate = useCallback(
    async (input: SaveFormTemplateInput, saveAnyway: boolean) => {
      const { templateToEdit } = input;
      if (!templateToEdit) return;

      const visibilityPayload =
        input.visibility === templateToEdit.template_visibility
          ? {}
          : { template_visibility: input.visibility };

      // Only broad-sharing admins can populate `shared_team_ids` (the team
      // picker is hidden otherwise), so an empty list means "no specific teams
      // selected" and we omit it to avoid clobbering teams set elsewhere.
      const sharedTeamsPayload =
        input.visibility === TemplateVisibility.Team && input.sharedTeamIds.length > 0
          ? { shared_team_ids: input.sharedTeamIds }
          : {};

      const formFieldsPayload =
        input.formFields.length > 0
          ? {
              form_fields: extractedFieldsToFormFields(
                input.formFields,
                input.pageDimensions
              ),
            }
          : {};

      await updateTemplate({
        template: {
          id: templateToEdit.id,
          is_private: true,
          name: input.name,
          team_id: teamDetails?.team.id,
          ...visibilityPayload,
          ...sharedTeamsPayload,
          ...formFieldsPayload,
        },
        saveAnyway,
      });
    },
    [teamDetails?.team.id, updateTemplate]
  );

  const createTemplate = useCallback(
    async (
      input: SaveFormTemplateInput,
      saveAnyway: boolean,
      options: CreateTemplateOptions
    ) => {
      const { isCopyFromProtectedTemplate, copyToastMessage } = options;

      if (!isCopyFromProtectedTemplate && !input.file) {
        throw new Error('PDF file is missing');
      }

      const objectKey =
        isCopyFromProtectedTemplate && input.templateToEdit?.s3_object_key
          ? input.templateToEdit.s3_object_key
          : generateFileKey(input.file?.name ?? input.name);

      if (!isCopyFromProtectedTemplate && input.file) {
        await uploadPdfToS3(objectKey, input.file);
      }

      const formFieldsForApi =
        input.formFields.length > 0
          ? extractedFieldsToFormFields(input.formFields, input.pageDimensions)
          : null;

      const templateVisibility = isCopyFromProtectedTemplate
        ? TemplateVisibility.User
        : input.visibility;

      const teamId =
        !isCopyFromProtectedTemplate &&
        teamDetails?.team.id &&
        templateVisibility === TemplateVisibility.Team
          ? teamDetails.team.id
          : null;

      // Only broad-sharing admins can populate `shared_team_ids` (the team
      // picker is hidden otherwise), so an empty list is treated as "no
      // specific teams selected" and omitted.
      const sharedTeamIds = input.sharedTeamIds;
      const sharedTeamIdsForApi =
        !isCopyFromProtectedTemplate &&
        templateVisibility === TemplateVisibility.Team &&
        sharedTeamIds.length > 0
          ? sharedTeamIds
          : undefined;

      const result = await createPDFTemplate({
        input: {
          s3_object_key: objectKey,
          template_visibility: templateVisibility,
          name: input.name,
          team_id: teamId,
          shared_team_ids: sharedTeamIdsForApi,
          form_fields: formFieldsForApi,
          source_template_id: isCopyFromProtectedTemplate
            ? input.templateToEdit?.id
            : undefined,
        },
        saveAnyway,
      });

      if (result?.data?.id) {
        setNewlyCreatedTemplate({ id: result.data.id, name: input.name });
        if (copyToastMessage) {
          toast({ title: intl.formatMessage(copyToastMessage) });
        }
      }

      setFormViewType(FormViewType.CREATE);
    },
    [
      createPDFTemplate,
      intl,
      setFormViewType,
      setNewlyCreatedTemplate,
      teamDetails?.team.id,
      uploadPdfToS3,
    ]
  );

  const saveFormTemplate = useCallback(
    async (input: SaveFormTemplateInput, saveAnyway: boolean): Promise<boolean> => {
      try {
        const isNonEditableTemplate = input.templateToEdit?.is_editable === false;

        const isEditMode =
          input.formViewType === FormViewType.EDIT &&
          input.templateToEdit &&
          !isNonEditableTemplate;

        if (isEditMode) {
          await editTemplate(input, saveAnyway);
        } else if (
          input.formViewType === FormViewType.EDIT &&
          input.templateToEdit &&
          isNonEditableTemplate
        ) {
          await createTemplate(input, saveAnyway, {
            isCopyFromProtectedTemplate: true,
            copyToastMessage: getCopyToastMessage(input.templateToEdit),
          });
        } else {
          await createTemplate(input, saveAnyway, {
            isCopyFromProtectedTemplate: false,
          });
        }

        return true;
      } catch (e) {
        logger.error(e);
        // Mutation delegates (`onRequestError`, `onUpdateFailed`, `onComplianceRejected`)
        // already surface UI for API failures; `mutateAsync` still rejects afterward.
        if (!isAxiosError(e)) {
          toast({
            title: intl.formatMessage({
              defaultMessage: 'Failed to save template',
              id: 'ylRAUEiSJC',
              description: 'Destructive toast when PDF form template save fails',
            }),
            tone: 'destructive',
          });
        }
        return false;
      }
    },
    [createTemplate, editTemplate, intl]
  );

  return { saveFormTemplate };
};
